// ============================================================================
// spectrumVideo.ts
// Image générée d'une source « audio seul » à partir de son son : un analyseur de
// spectre (fréquences) ou un oscilloscope (forme d'onde).
//
// Le flux vidéo est un canvas repeint à 25 i/s depuis les analyseurs de l'entrée
// `audio-<id>` de l'AudioSourceRegistry (getAnalysers : après volume, égaliseur et
// gains L/R — ce qu'on voit est donc ce qui part à l'antenne). Les analyseurs sont
// relus à CHAQUE image : l'entrée peut naître après le flux (micro en cours
// d'autorisation) ou être recréée sous le même id (changement d'entrée audio).
//
// Un flux par source, partagé : mini-vue, Preview et antenne lisent le même
// (acquireSpectrumStream / releaseSpectrumStream). La fenêtre de réglage, elle,
// crée le sien (createSpectrumVideoStream) le temps de l'aperçu.
// ============================================================================

import type { AudioSourceAnalysers } from './audioPipeline/AudioSourceRegistry';

const FPS      = 25;
const BARS     = 64;
const F_MIN    = 30;      // Hz — en dessous, rien d'utile pour la voix ou la musique
const F_MAX    = 16000;   // Hz
const PEAK_FALL = 0.012;  // chute du repère de crête, en fraction de hauteur par image

export type AnalysersGetter = () => AudioSourceAnalysers | null;
export type AudioVisualMode = 'spectrum' | 'oscilloscope';

export interface SpectrumVideo {
  stream: MediaStream;
  mode  : AudioVisualMode;
  stop  : () => void;
}

export function createSpectrumVideoStream(
  getAnalysers: AnalysersGetter,
  width  = 1280,
  height = 720,
  mode: AudioVisualMode = 'spectrum',
): SpectrumVideo {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const peaks = new Float32Array(BARS);
  let bufL: Uint8Array | null = null;
  let bufR: Uint8Array | null = null;

  const gradient = ctx.createLinearGradient(0, height, 0, 0);
  gradient.addColorStop(0.00, '#0e7490');
  gradient.addColorStop(0.55, '#22d3ee');
  gradient.addColorStop(0.80, '#facc15');
  gradient.addColorStop(1.00, '#ef4444');

  let wave: Float32Array | null = null;
  let waveR: Float32Array | null = null;

  // ── Oscilloscope ──────────────────────────────────────────────────────────
  // Moyenne L/R, déclenchée sur un passage par zéro montant pour que la trace ne
  // défile pas d'une image à l'autre. On montre la moitié du tampon (≈ 21 ms à
  // 48 kHz) : le déclenchement trouve toujours de quoi remplir la largeur.
  const paintOscilloscope = (an: AudioSourceAnalysers | null) => {
    const midY = height / 2;
    const amp  = height * 0.42;

    // Réticule discret : axe central et quatre graduations.
    ctx.strokeStyle = 'rgba(148,163,184,0.12)';
    ctx.lineWidth   = Math.max(1, height * 0.0015);
    for (let k = 1; k < 4; k++) {
      const y = (height / 4) * k;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }
    for (let k = 1; k < 8; k++) {
      const x = (width / 8) * k;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
    }

    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth   = Math.max(2, height * 0.004);
    ctx.shadowColor = 'rgba(34,211,238,0.6)';
    ctx.shadowBlur  = height * 0.012;
    ctx.beginPath();
    if (!an) {
      ctx.moveTo(0, midY); ctx.lineTo(width, midY);
    } else {
      const n = an.analyserL.fftSize;
      if (!wave || wave.length !== n) { wave = new Float32Array(n); waveR = new Float32Array(n); }
      an.analyserL.getFloatTimeDomainData(wave);
      an.analyserR.getFloatTimeDomainData(waveR!);
      for (let i = 0; i < n; i++) wave[i] = (wave[i] + waveR![i]) / 2;

      const span = Math.floor(n / 2);
      let start = 0;
      for (let i = 1; i < n - span; i++) {
        if (wave[i - 1] < 0 && wave[i] >= 0) { start = i; break; }
      }
      for (let i = 0; i < span; i++) {
        const x = (i / (span - 1)) * width;
        const y = midY - Math.max(-1, Math.min(1, wave[start + i])) * amp;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
  };

  const paint = () => {
    ctx.fillStyle = '#05070d';
    ctx.fillRect(0, 0, width, height);

    const an = getAnalysers();
    if (mode === 'oscilloscope') { paintOscilloscope(an); return; }
    const marginX = width * 0.05;
    const baseY   = height * 0.88;
    const maxH    = height * 0.76;
    const slot    = (width - 2 * marginX) / BARS;
    const barW    = slot * 0.72;

    // Ligne de base, toujours dessinée : une source muette se voit comme telle.
    ctx.fillStyle = 'rgba(148,163,184,0.25)';
    ctx.fillRect(marginX, baseY + 2, width - 2 * marginX, Math.max(1, height * 0.003));

    if (an) {
      const n = an.analyserL.frequencyBinCount;
      if (!bufL || bufL.length !== n) { bufL = new Uint8Array(n); bufR = new Uint8Array(n); }
      an.analyserL.getByteFrequencyData(bufL);
      an.analyserR.getByteFrequencyData(bufR!);
      const binHz = an.analyserL.context.sampleRate / an.analyserL.fftSize;

      ctx.fillStyle = gradient;
      for (let b = 0; b < BARS; b++) {
        // Bandes en échelle logarithmique, comme l'oreille : une échelle linéaire
        // tasserait toute la voix dans les trois premières barres.
        const f0 = F_MIN * Math.pow(F_MAX / F_MIN, b / BARS);
        const f1 = F_MIN * Math.pow(F_MAX / F_MIN, (b + 1) / BARS);
        const i0 = Math.max(0, Math.floor(f0 / binHz));
        const i1 = Math.min(n - 1, Math.max(i0, Math.ceil(f1 / binHz)));
        let sum = 0;
        for (let i = i0; i <= i1; i++) sum += Math.max(bufL[i], bufR![i]);
        const v = sum / ((i1 - i0 + 1) * 255);

        const h = v * maxH;
        const x = marginX + b * slot + (slot - barW) / 2;
        ctx.fillRect(x, baseY - h, barW, h);

        peaks[b] = Math.max(v, peaks[b] - PEAK_FALL);
      }
      ctx.fillStyle = '#e2e8f0';
      for (let b = 0; b < BARS; b++) {
        if (peaks[b] <= 0.01) continue;
        const x = marginX + b * slot + (slot - barW) / 2;
        ctx.fillRect(x, baseY - peaks[b] * maxH - height * 0.008, barW, Math.max(2, height * 0.005));
      }
    }
  };

  paint();
  const stream = canvas.captureStream(FPS);
  const track  = stream.getVideoTracks()[0];
  const timer  = setInterval(() => {
    if (!track || track.readyState === 'ended') { clearInterval(timer); return; }
    paint();
  }, 1000 / FPS);

  return {
    stream,
    mode,
    stop: () => { clearInterval(timer); stream.getTracks().forEach(t => t.stop()); },
  };
}

// ── Flux partagés, un par source ──────────────────────────────────────────────

const shared = new Map<string, SpectrumVideo>();

export function acquireSpectrumStream(
  sourceId: string, getAnalysers: AnalysersGetter, width?: number, height?: number, mode: AudioVisualMode = 'spectrum',
): MediaStream {
  let entry = shared.get(sourceId);
  if (entry && entry.mode !== mode) { entry.stop(); entry = undefined; }
  if (!entry || entry.stream.getVideoTracks()[0]?.readyState === 'ended') {
    entry = createSpectrumVideoStream(getAnalysers, width, height, mode);
    shared.set(sourceId, entry);
  }
  return entry.stream;
}

export function getSpectrumStream(sourceId: string): MediaStream | null {
  return shared.get(sourceId)?.stream ?? null;
}

export function releaseSpectrumStream(sourceId: string): void {
  const entry = shared.get(sourceId);
  if (!entry) return;
  shared.delete(sourceId);
  entry.stop();
}
